import React, {useContext, useState} from "react";
import ConfiguratorContext from "./ConfiguratorContext";
import {FormattedMessage, useIntl} from "react-intl";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon
} from "@heroicons/react/24/outline";
import Currency from "./Currency";


// Constants
const DETAIL_CONTAINER_STYLE = {borderLeft: '5px solid #0001'};


const UndefinedPricePlaceholder = ({summary}) => {
  const cls = summary ? 'text-yellow-500 text-base' : 'text-yellow-600 text-xs';
  return (
    <span className={`flex gap-1 items-center ${cls}`}>
      <ExclamationCircleIcon className={summary ? 'w-6' : 'w-4'}/>
      <span className="whitespace-nowrap">
        <FormattedMessage id="undefined" />
      </span>
    </span>
  )
}


const ConfiguratorSurveyOutputKCC = ({
  results,
  loading,
  summary,
}) => {

  // context
  const context = useContext(ConfiguratorContext);

  // hooks
  const intl = useIntl();

  // state
  const [minimizeParameters, setMinimizeParameters] = useState(false);
  const [minimizeDetail, setMinimizeDetail] = useState(false);

  // utilities
  const isTintometerService = context?.service?.type === 'tintometer';

  // utilities - icons
  const ParametersChevronIcon =
    minimizeParameters ? ChevronDownIcon : ChevronUpIcon;
  const DetailChevronIcon =
    minimizeDetail ? ChevronDownIcon : ChevronUpIcon;

  // utilities - responses
  const selectedColor = !!results
    ? context?.colors?.find((c) => c.code === results.cod_color)
    : null;
  const selectedSystem = !!results
    ? context.configuration
      .find((c) => c.type === 'option' && c.system_id === results.cod_system)
      ?.system
    : null;
  const selectedProductLists = ['primers', 'reti', 'rasanti'].reduce(
    (prev, curr) => ({
      ...prev,
      [curr]: !!results
        ? context.configuration
          .filter((c) => c.type === 'option' && results[`lst_${curr}`]?.includes(c.product?.art_cod))
          .map((c) => c.product)
        : null
    }),
    {}
  )

  return (
    <div className="mb-2">
      {loading && (
        <p className={`${summary ? 'mt-2 text-base' : 'mt-1 text-xs'} font-gray-400`}>
          <FormattedMessage id="kcc_computation_loading_dialog" />
        </p>
      )}
      {!!results && <>
        <div className="mt-4">
          <h3 className={`flex items-center ${summary ? 'gap-3 text-2xl mb-4' : 'gap-2 text-lg mb-1'}`}>
            <FormattedMessage id="parameters" />
            <ParametersChevronIcon
              className={`${summary ? 'w-6' : 'w-4'} cursor-pointer`}
              onClick={() => { setMinimizeParameters((prev) => !prev); }}
            />
          </h3>
          {!minimizeParameters && (
            <div className={`mt-2 ${summary ? 'py-5 px-6 text-lg' : 'py-3 px-4 text-base'}`} style={DETAIL_CONTAINER_STYLE}>
              <div className={`flex flex-col ${summary ? 'gap-4' : 'gap-3'}`}>
                <div className="flex items-center gap-3">
                  <span>{intl.formatMessage({id: 'color'})}:</span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-5"
                      style={{
                        border: '1px solid #fff5',
                        backgroundColor: selectedColor ? `rgb(${selectedColor.rgb.join(', ')})` : '#fff2',
                        aspectRatio: '1'
                      }}
                    />
                    <span className="font-bold">
                      {'KK ' + selectedColor?.code?.replace('KK', '')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span>{intl.formatMessage({id: 'system'})}:</span>
                  <span className="font-bold">{selectedSystem?.ciclo}</span>
                </div>
                {Object.entries(selectedProductLists).map(([target, products]) => (
                  <div key={target} className="flex items-start gap-3">
                    <span>{intl.formatMessage({id: target})}:</span>
                    {
                      products?.length > 0
                        ? <ul className="list-none">
                          {
                            products?.map((p, idx) => (
                              <li key={idx} className="font-bold">
                                {p.art_des_cmpy}
                              </li>
                            ))
                          }
                        </ul>
                        : <span className="font-bold">
                          {intl.formatMessage({id: 'none'})}
                        </span>
                    }
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <span><FormattedMessage id="surface" />:</span>
                  <span className="font-bold">{results.val_surface} mq</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="my-5">
          <h3 className={`flex items-center ${summary ? 'gap-3 text-2xl mb-2' : 'gap-2 text-lg mb-1'}`}>
            <FormattedMessage id="expenses_detail" />
            <DetailChevronIcon
              className={`${summary ? 'w-6' : 'w-4'} cursor-pointer`}
              onClick={() => { setMinimizeDetail((prev) => !prev); }}
            />
          </h3>
          <h4 className={`italic ${summary ? 'text-base' : 'text-xs'}`}>
            <FormattedMessage id="expenses_detail_helper" />
          </h4>
          <div className={summary ? 'pl-6 mt-4' : 'pl-4 mt-2'} style={DETAIL_CONTAINER_STYLE}>
            {
              !minimizeDetail && (
                results.value.some((p) => Object.values(p).length > 0)
                  ? results.value.map((p, idx, arr) => {
                    // skip if object is empty
                    if (Object.keys(p).length === 0)
                      return null;
                    // return rendered object
                    return (
                      <div
                        key={idx}
                        className={summary ? 'pt-5 pb-4' : 'pt-4 pb-3'}
                        style={idx < arr.length - 1 ? {borderBottom: '2px solid #0001'} : undefined}
                      >
                        <div className={`flex items-start gap-4 justify-between ${summary ? 'text-lg' : 'text-base'}`}>
                          <span className="font-bold">{p.entry_code} - {p.entry_description}</span>
                          {
                            p.entry_total_price
                              ? <Currency value={p.entry_total_price}/>
                              : <UndefinedPricePlaceholder summary={summary} />
                          }
                        </div>
                        <div className={summary ? 'my-2 text-base' : 'my-1 text-xs'}>
                          <p>
                            <FormattedMessage id="quantity" />: {p.entry_qty} {p.entry_mu}
                          </p>
                          <p className="flex gap-1 items-center">
                            <span>{intl.formatMessage({id: 'unit_price'})}:</span>
                            <span>
                              {
                                p.entry_unit_price
                                  ? <span><Currency value={p.entry_unit_price}/>/{p.entry_mu}</span>
                                  : <UndefinedPricePlaceholder summary={summary} />
                              }
                            </span>
                          </p>
                          {
                            isTintometerService && p.entry_qty_per_package && (
                              <p className="text-green-700">
                                <FormattedMessage id="packages_to_purchase" />: {p.entry_qty_per_package}
                              </p>
                            )
                          }
                        </div>
                      </div>
                    )
                  })
                  : (
                    <div className={
                      "flex gap-2 items-center "
                      + (summary ? "py-5 text-lg text-yellow-500" : "py-4 text-base text-yellow-600")
                    }>
                      <ExclamationCircleIcon className={summary ? 'w-6' : 'w-4'} />
                      <span><FormattedMessage id="no_products_retrieved" /></span>
                    </div>
                  )
              )
            }
          </div>
        </div>
      </>}
    </div>
  )
};

export default ConfiguratorSurveyOutputKCC;
